<template>
    <v-layout column fill-height>
        <v-flex xs12>
            <v-layout row wrap>
                <div aria-live="polite">
                    <template v-if="finished">
                        <v-flex xs12 pb-3>
                            <h1 class="headline">{{$t('REGISTRATION_SIGNUP_FINISHED_TITLE')}}</h1>
                        </v-flex>
                        <v-flex xs12>
                            <template v-if="finished">
                                <p class="mt-4 mb-4" v-html="$t('SIGNUP_SUBMISSION_COMPLETED_TEXT')"/>
                            </template>
                        </v-flex>
                    </template>
                </div>
                <template v-if="!finished">
                <v-flex xs12 pb-3>
                    <h1 class="headline">{{$t('REGISTRATION_SIGNUP_FORM_TITLE')}}</h1>
                </v-flex>
                <v-flex xs12>
                    <template v-if="!validated">
                        <v-layout row wrap>
                            <v-flex xs12>
                                <v-text-field
                                    :label="`${$t('INVITATION_ACCOUNT_REQUEST_ORGANIZATION_CODE')}*`"
                                    v-model="code"
                                    :disabled="loading"
                                    @input="attemptedValidation = false"
                                    @change="validateOrganizationCode()"
                                    :error-messages="!validated && !attemptedValidation ? '' : $t('INVITATION_ACCOUNT_REQUEST_INVALID_CODE_MESSAGE')"
                                    required
                                />
                            </v-flex>
                        </v-layout>
                    </template>

                    <template v-else>
                        <ErrorDialog
                            v-if="mailChimpError"
                            :error="`${$t('REGISTRATION_NEWSLETTER_ERROR')}`"
                            @confirm="mailChimpError=false"
                        />
                        <AccessibleForm :submit-function="confirm" form-instruction>
                            <p
                                class="mt-3 mb-3 subheading"
                                v-html="$t('REGISTRATION_SIGNUP_FORM_INSTRUCTIONS', {organization: selectedOrganization.organizationName})"
                            />
                            <v-text-field
                                v-model="firstName"
                                :label="`${$t('REGISTRATION_FIRST_NAME')}*`"
                                required
                                :error-messages="errors.collect('first name')"
                                :disabled="loading"
                                v-validate="`required|max:${maxFieldLength}`"
                                data-vv-name="first name"
                                autocomplete="given-name"
                            />
                            <v-text-field
                                v-model="lastName"
                                :label="`${$t('REGISTRATION_LAST_NAME')}*`"
                                required
                                :error-messages="errors.collect('last name')"
                                :disabled="loading"
                                v-validate="`required|max:${maxFieldLength}`"
                                data-vv-name="last name"
                                autocomplete="family-name"
                            />
                            <v-text-field
                                v-model="invitation.email"
                                :label="`${$t('INVITATION_ACCOUNT_REQUEST_EMAIL_TITLE')}*`"
                                required
                                :error-messages="errors.collect('email')"
                                :disabled="loading"
                                v-validate="`required|email|max:${maxFieldLength}`"
                                data-vv-name="email"
                                autocomplete="email"
                            />
                            <v-text-field
                                v-model="invitation.title"
                                :label="`${$t('INVITATION_JOB_TITLE')}*`"
                                :disabled="loading"
                                required
                                :error-messages="errors.collect('title')"
                                v-validate="`required|max:${maxFieldLength}`"
                                data-vv-name="title"
                            />
                            <accessible-combobox
                                v-if="organizationBusinessUnits.length > 1"
                                :items="organizationBusinessUnits"
                                item-text="displayText"
                                item-value="id"
                                v-model="selectedSchool"
                                :label="`${businessUnitDisplayName(selectedOrganization)}*`"
                                :error-messages="businessUnitSelectorErrorMessage('businessUnitDisplaySelector')"
                                :disabled="loading"
                                :required="!creatingBusinessUnit"
                                v-validate="!creatingBusinessUnit? `required`: ''"
                                :data-vv-name="'businessUnitDisplaySelector'"
                            />
                            <v-text-field
                                v-if="creatingBusinessUnit"
                                v-model="invitation.businessUnit"
                                :label="`${businessUnitDisplayName(selectedOrganization)}*`"
                                :disabled="loading"
                                required
                                v-validate="`required|max:${maxFieldLength}`"
                                :error-messages="errors.collect(businessUnitDisplayName(selectedOrganization).toLowerCase())"
                                :data-vv-name="businessUnitDisplayName(selectedOrganization).toLowerCase()"
                            />
                            <accessible-combobox
                                v-if="creatingBusinessUnit"
                                v-model="selectedBusinessRegion"
                                :label="`${businessUnitDisplayRegion(selectedOrganization)}*`"
                                :items="organizationRegions"
                                :disabled="loading"
                                :error-messages="businessUnitSelectorErrorMessage('businessUnitRegionSelector')"
                                :required="creatingBusinessUnit && !creatingBusinessUnitRegion"
                                v-validate="creatingBusinessUnit && !creatingBusinessUnitRegion? `required`: ''"
                                :data-vv-name="'businessUnitRegionSelector'"
                            />
                            <v-text-field
                                v-if="creatingBusinessUnit && creatingBusinessUnitRegion"
                                v-model="invitation.businessRegion"
                                :label="`${businessUnitDisplayRegion(selectedOrganization)}*`"
                                required
                                :disabled="loading"
                                :error-messages="businessUnitErrorMessage('businessUnitDisplayRegion')"
                                v-validate="`required|max:${maxFieldLength}`"
                                data-vv-name="'businessUnitDisplayRegion'"
                            />
                            <v-select
                                v-if="creatingBusinessUnit"
                                v-model="invitation.businessCategory"
                                :label="`${businessUnitDisplayCategory(selectedOrganization)}*`"
                                :items="businessUnitCategories"
                                :disabled="loading"
                                required
                                item-text="key"
                                item-value="value"
                                :error-messages="businessUnitErrorMessage('businessUnitDisplayCategory')"
                                v-validate="`required`"
                                :data-vv-name="'businessUnitDisplayCategory'"
                            />
                            <v-checkbox
                                v-model="newsLetterChecked"
                                class="mt-3 pt-0"
                                :disabled="loading"
                                label="Signup for monthly newsletter"
                            />
                            <Honeypot v-model="honeypotFieldValue"/>
                            <v-layout row wrap>
                                <router-link class="pt-3" to="/">{{$t("REGISTRATION_ALREADY_REGISTERED")}}</router-link>
                                <v-spacer/>
                                <v-btn
                                    :disabled="loading"
                                    :dark="!loading"
                                    color="cancel"
                                    @click="cancel"
                                    class="elevation-0"
                                    :aria-label="$t('REGISTRATION_SIGNUP_CANCEL_BUTTON')"
                                    >{{$t('REGISTRATION_SIGNUP_CANCEL_BUTTON')}}</v-btn>
                                <v-btn
                                    type="submit"
                                    :disabled="loading"
                                    :dark="!loading"
                                    :loading="loading"
                                    color="confirm"
                                    class="elevation-0 ml-2"
                                    :aria-label="$t('REGISTRATION_SIGNUP_CONFIRM_BUTTON')"
                                    >{{$t('REGISTRATION_SIGNUP_CONFIRM_BUTTON')}}</v-btn>
                            </v-layout>
                        </AccessibleForm>
                    </template>
                </v-flex>
                </template>
            </v-layout>
        </v-flex>
        <v-spacer/>
        <v-flex xs12 v-if="!validated">
            <v-layout row wrap>
                <v-flex xs12 sm8 md8>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <span v-on="on" class="tooltipClass" style="line-height:36px; height:36px;">
                                {{$t('SIGNUP_ORGANIZATION_CODE_INFO')}}
                            </span>
                        </template>
                        <span>{{$t('SIGNUP_ORGANIZATION_CODE_TOOLTIP')}}</span>
                    </v-tooltip>
                </v-flex>
                <v-spacer/>
                    <v-btn
                        @click="validateOrganizationCode()"
                        color='primary'
                        class="text-xs-center elevation-0 ma-0"
                        :aria-label="$t('NEXT')"
                    >
                        {{validatingCode? '' : $t('NEXT')}}
                        <v-progress-circular indeterminate v-if="validatingCode" color="primary"/>
                    </v-btn>
            </v-layout>
        </v-flex>
    </v-layout>
</template>

<script lang="ts">

import Vue from "vue";
import { IInvitation } from "@cyber-range/cyber-range-api-invitation-client";
import { UserRole } from "@cyber-range/cyber-range-api-user-client";
import Component from "vue-class-component";
import Config from '@/config';
import { useApiClientStore } from "@/stores/apiClientStore";
import { useOrganizationStore } from "@/stores/organizationStore";
import ErrorDialog from '@/components/utils/ErrorDialog.vue'
import { BusinessUnitCategory, IBusinessUnit } from "@cyber-range/cyber-range-api-business-unit-client";
import { IOrganizationCode } from "@cyber-range/cyber-range-api-organization-client";
import { Subscriber, EmailFormat, Topic } from "@cyber-range/cyber-range-api-newsletter-client";
import Domain from '@interfaces/domain';
import { IApiPageResponse } from "@cyber-range/cyber-range-api-client";
import { validateAll } from '@utils/validateAll';
import AccessibleCombobox from '@components/utils/AccessibleCombobox.vue';
import AccessibleForm from '@components/utils/AccessibleForm.vue'
import Honeypot from '@components/utils/Honeypot.vue';

@Component({
    components: { ErrorDialog, AccessibleCombobox, AccessibleForm }
})
export default class UscrInstructorSignUp extends Vue {
    get invitationApiClient() { return useApiClientStore().invitationApiClient; }
    get businessUnitApiClient() { return useApiClientStore().businessUnitApiClient; }
    get organizaztionApiClient() { return useApiClientStore().backgroundOrganizationApiClient; }
    get newsletterApiClient() { return useApiClientStore().newsletterApiClient; }
    get businessUnitDisplayName() { return useOrganizationStore().businessUnitDisplayName; }
    get businessUnitDisplayRegion() { return useOrganizationStore().businessUnitDisplayRegion; }
    get businessUnitDisplayCategory() { return useOrganizationStore().businessUnitDisplayCategory; }
    get loading() { return useApiClientStore().isLoading; }
    //state management
    mailChimpError:boolean = false;
    firstName:string = '';
    lastName:string = '';
    finished = false;
    validated = false;
    attemptedValidation = false; 
    validatingCode = false;
    submissionAttempted: boolean = false;
    maxFieldLength = 255;
    honeypotFieldValue = ""
    //selection population
    selectedOrganization: IOrganizationCode = <IOrganizationCode>{
        organizationId: "",
        organizationName: "",
        value: "",
        customAttributes: []
    };

    cancel(){
        this.code = '';
        this.validated = false;
        this.attemptedValidation = false;
        this.$emit('cancel');
    }

    businessUnitSelectorErrorMessage(field: string): string | string[] {
        return this.$validator.errors.first(field) && (!this.creatingBusinessUnit || !this.creatingBusinessUnitRegion)
            ? <string>this.$root.$t('BUSINESS_UNIT_FIELD_REQUIRED_ERROR_TEXT', {field: this.businessUnitFieldName(field)})
            : [];
    }

    businessUnitErrorMessage(field: string): string | string[] {
        return this.$validator.errors.first(field) && this.creatingBusinessUnit
            ? <string>this.$root.$t('BUSINESS_UNIT_FIELD_REQUIRED_ERROR_TEXT', {field: this.businessUnitFieldName(field)})
            : [];
    }

    businessUnitFieldName(field: string): string {
        if (field === "businessUnitDisplaySelector") {
            field = "businessUnitDisplayName";
        }
        if (field === "businessUnitRegionSelector") {
            field = "businessUnitDisplayRegion";
        }
        return this[field](this.selectedOrganization).toLowerCase();
    }

    updateCodeValidation(): void {
        this.attemptedValidation = false;
    }

    selectedSchool: string = "";
    selectedBusinessRegion: string = "";
    businessUnits = [];
    newsLetterChecked: boolean = true;

    //When Creating a Businuess Unit
    readonly otherSchoolStub = {
        name: "Other",
        region: "Other",
        id: "otherSchoolId",
        displayText: "Other"
    };

    code = "";
    invitation: IInvitation = <any>{
        name: "",
        email: "",
        businessUnit: "",
        businessRegion: "",
        businessCategory: "",
        title: "",
        businessUnitId: "",
        organizationId: ""
    };

    get creatingBusinessUnit(): boolean {
        return this.selectedSchool === this.otherSchoolStub.id;
    }

    get creatingBusinessUnitRegion(): boolean {
        return this.selectedBusinessRegion === this.otherSchoolStub.region;
    }

    async validateOrganizationCode(attemptedCode?: string): Promise<void> {
        this.validatingCode = this.code ? true : false;
        attemptedCode = attemptedCode || this.code;

        if(attemptedCode.length === 0){
            this.attemptedValidation = true;
            this.validatingCode = false;
            return;
        }

        try {
            const code = await this.organizaztionApiClient.getOrganizationCode(attemptedCode);
            this.selectedOrganization = code;
            this.invitation.organizationId = code.organizationId;
            this.validated = true;
            this.$emit('submitting', true);
        } catch (e) {
            this.validated = false;
        }
        if (this.code !== "") {
            this.attemptedValidation = true;
        }
        this.validatingCode = false;
    }

    get businessUnitCategories(): {key: string, value: string}[] {
        return Object.keys(BusinessUnitCategory).map(key => ({key: key, value: BusinessUnitCategory[key]}));
    }

    get pageDomain(): string {
        return window.location.hostname.split(".")[0];
    }

    get queryCode(): string {
        return Array.isArray(this.$route.query.code) ? this.$route.query.code[0] : this.$route.query.code;
    }

    async created() {
        let request: IApiPageResponse<IBusinessUnit>;
        do
        {
            request = await this.businessUnitApiClient.get({
                token: request?.nextPageToken
            });
            this.businessUnits.push(...request.items);
        }
        while ( request?.nextPageToken )

        for (let bu of this.businessUnits) {
            bu["displayText"] = `${bu.name} : ${bu.region}`;
        }

        this.businessUnits = this.businessUnits.sort((a, b) => {
            return a.displayText < b.displayText ? -1 : 1;
        });

        let code = this.queryCode || this.pageDomain;

        if (code) {
            await this.validateOrganizationCode(code);
        }
    }

    get organizationBusinessUnits(): IBusinessUnit[] {
        let businessUnits = this.businessUnits.filter(
            bu => bu.organizationId === this.selectedOrganization.organizationId
        ).map(bu=>Object.assign({displayText:`${bu.name} : ${bu.region}`},bu ));
        businessUnits.push(this.otherSchoolStub);

        return businessUnits;
    }

    get organizationRegions(): string[] {
        return this.organizationBusinessUnits.map(bu => bu.region);
    }

    async confirm() {
        this.invitation.name = (`${this.firstName} ${this.lastName}`).trim();

        if (this.honeypotFieldValue)
        {
            this.finished = true;
            return;
        }

        if (await validateAll(this)) {
            if (this.invitation.businessUnit === "") {
                this.invitation.businessUnit = this.selectedSchool;
            }
            if (this.invitation.businessRegion === "") {
                this.invitation.businessRegion = this.selectedBusinessRegion;
            }
            let chosenSchool: IBusinessUnit = this.businessUnits.find(bu => bu.id === this.selectedSchool);

            if (chosenSchool !== undefined) {
                this.invitation.businessRegion = chosenSchool.region;
                this.invitation.businessCategory = <any>chosenSchool.category;
                this.invitation.businessUnit = chosenSchool.name;
                this.invitation.businessUnitId = chosenSchool.id;
            }

            if (this.creatingBusinessUnit) {
                delete this.invitation.businessUnitId;
            }
            //This is here because if the length is 1, that means other is the only option,
            //So we can't create an invitation with a business unit reference.
            if (this.organizationBusinessUnits.length === 1) {
                delete this.invitation.businessUnitId;
                delete this.invitation.businessUnit;
                delete this.invitation.businessCategory;
                delete this.invitation.businessRegion;
            }
            this.invitation.roles = [
                    UserRole.OrganizationInstructor
            ]
            if(this.newsLetterChecked)
            {
                try{
                    await this.newsletterApiClient.create(
                        new Subscriber(
                            {
                                name: this.invitation.name,
                                emailAddress: this.invitation.email,
                                businessUnit: this.invitation.businessUnit,
                                emailFormat: EmailFormat.HTML,
                                jobTitle: this.invitation.title,
                                topics: [(Config.CYBER_RANGE_UI_BASE_URL.includes(Domain.USCR)?Topic.UscrGeneral:Topic.VacrGeneral)]
                            }
                        )
                    )
                }
                catch(e)
                {
                    if(e.message.includes("looks fake or invalid"))
                    {
                        this.mailChimpError = true;
                    }
                    else
                    {
                        throw e
                    }
                }
                
              
            }
            if(!this.mailChimpError)
            {
                await this.invitationApiClient.add([this.invitation]);
                this.finished = true;
            }
           
        }else{
            this.submissionAttempted = true;
        }
    }
}
</script>

<style>
.v-input--selection-controls {
    margin-top: 0px;
    padding-top: 0px;
}

.groupClass {
    background-color: rgba(39, 52, 116, 0.2);
    border-radius: 6px;
    min-height: 400px;
    overflow: hidden;
}

.rightSideClass {
    background-color: rgba(39, 52, 116, 0.3);
}

.tooltipClass {
    color: blue;
}
</style>
